// utility functions
function triggerClick (el) {
  el.dispatchEvent(new CustomEvent('click', {
    bubbles: true,
    cancelable: true
  }))
}

function triggerChange (el) {
  el.dispatchEvent(new CustomEvent('change', {
    bubbles: true,
    cancelable: false
  }))
}

function triggerFocusIn (el) {
  el.dispatchEvent(new CustomEvent('focusin', {
    bubbles: true,
    cancelable: false
  }))
}

function triggerFocusOut (el) {
  el.dispatchEvent(new CustomEvent('focusout', {
    bubbles: true,
    cancelable: false
  }))
}

function attr (el, key) {
  return el.getAttribute(key)
}

function hasClass (el, className) {
  return el?.classList.contains(className) || false
}

function addClass (el, className) {
  return el?.classList.add(className)
}

function removeClass (el, className) {
  return el?.classList.remove(className)
}

const defaultOptions = {
  data: null,
  searchable: false
}
export default function NiceSelect (element, options) {
  this.el = element
  this.config = Object.assign({}, defaultOptions, options || {})
  this.data = this.config.data
  this.selectedOptions = []

  this.placeholder =
    attr(this.el, 'placeholder') ||
    this.config.placeholder ||
    'Select an option'

  this.dropdown = null
  this.multiple = attr(this.el, 'multiple')
  this.disabled = attr(this.el, 'disabled')

  this.create()
}

NiceSelect.prototype.create = function () {
  this.el.style.display = 'none'
  if (this.data) {
    this.processData(this.data)
  } else {
    this.extractData()
  }

  this.renderDropdown()
  this.bindEvent()
}

NiceSelect.prototype.processData = function (data) {
  const options = []
  data.forEach((item) => {
    options.push({
      data: item,
      attributes: {
        selected: false,
        disabled: false,
        optgroup: item.value === 'optgroup'
      }
    })
  })
  this.options = options
}

NiceSelect.prototype.extractData = function () {
  const options = this.el.querySelectorAll('option,optgroup')
  const data = []
  const allOptions = []
  const selectedOptions = []

  options.forEach((item) => {
    let itemData = null

    if (item.tagName === 'OPTGROUP') {
      itemData = {
        text: item.label,
        value: 'optgroup'
      }
    } else {
      itemData = {
        text: item.innerText,
        value: item.value
      }
    }

    const attributes = {
      selected: item.getAttribute('selected') != null,
      disabled: item.getAttribute('disabled') != null,
      optgroup: item.tagName === 'OPTGROUP'
    }

    data.push(itemData)
    allOptions.push({ data: itemData, attributes })
  })

  this.data = data
  this.options = allOptions
  this.options.forEach(function (item) {
    if (item.attributes.selected) { selectedOptions.push(item) }
  })

  this.selectedOptions = selectedOptions
}

NiceSelect.prototype.renderDropdown = function () {
  const classes = [
    'nice-select',
    attr(this.el, 'class') || '',
    this.disabled ? 'disabled' : '',
    this.multiple ? 'has-multiple' : ''
  ]

  const searchHtml = `<div class="nice-select-search-box">
<input type="text" class="nice-select-search" placeholder="Search..."/>
</div>`

  const html = `<div class="${classes.join(' ')}" tabindex="${
    this.disabled ? null : 0
  }">
  <span class="${this.multiple ? 'multiple-options' : 'current'}"></span>
  <div class="nice-select-dropdown">
  ${this.config.searchable ? searchHtml : ''}
  <ul class="list"></ul>
  </div></div>
`

  this.el.insertAdjacentHTML('afterend', html)

  this.dropdown = this.el.nextElementSibling
  this._renderSelectedItems()
  this._renderItems()
}

NiceSelect.prototype._renderSelectedItems = function () {
  if (this.multiple) {
    let selectedHtml = ''
    if (window.getComputedStyle(this.dropdown).width === 'auto' || this.selectedOptions.length < 2) {
      this.selectedOptions.forEach(function (item) {
        selectedHtml += `<span class="current">${item.data.text}</span>`
      })
      selectedHtml = selectedHtml === '' ? this.placeholder : selectedHtml
    } else {
      selectedHtml = this.selectedOptions.length + ' selected'
    }

    this.dropdown.querySelector('.multiple-options').innerHTML = selectedHtml
  } else {
    const html =
      this.selectedOptions.length > 0
        ? this.selectedOptions[0].data.text
        : this.placeholder

    this.dropdown.querySelector('.current').innerHTML = html
  }
}

NiceSelect.prototype._renderItems = function () {
  const ul = this.dropdown.querySelector('ul')
  this.options.forEach((item) => {
    ul.appendChild(this._renderItem(item))
  })
}

NiceSelect.prototype._renderItem = function (option) {
  const el = document.createElement('li')
  el.innerHTML = option.data.text
  if (option.attributes.optgroup) {
    el.classList.add('optgroup')
  } else {
    el.setAttribute('data-value', option.data.value)
    const classList = [
      'option',
      option.attributes.selected ? 'selected' : null,
      option.attributes.disabled ? 'disabled' : null
    ]

    el.addEventListener('click', this._onItemClicked.bind(this, option))
    el.classList.add(...classList)
  }

  option.element = el
  return el
}

NiceSelect.prototype.update = function () {
  this.extractData()
  if (this.dropdown) {
    const open = hasClass(this.dropdown, 'open')
    this.dropdown.parentNode.removeChild(this.dropdown)
    this.create()

    if (open) {
      triggerClick(this.dropdown)
    }
  }
}

NiceSelect.prototype.disable = function () {
  if (!this.disabled) {
    this.disabled = true
    addClass(this.dropdown, 'disabled')
  }
}

NiceSelect.prototype.enable = function () {
  if (this.disabled) {
    this.disabled = false
    removeClass(this.dropdown, 'disabled')
  }
}

NiceSelect.prototype.clear = function () {
  this.selectedOptions = []
  this._renderSelectedItems()
  this.updateSelectValue()
  triggerChange(this.el)
}

NiceSelect.prototype.destroy = function () {
  if (this.dropdown) {
    this.dropdown.parentNode.removeChild(this.dropdown)
    this.el.style.display = ''
  }
}

NiceSelect.prototype.bindEvent = function () {
  this.dropdown.addEventListener('click', this._onClicked.bind(this))
  this.dropdown.addEventListener('keydown', this._onKeyPressed.bind(this))
  this.dropdown.addEventListener('focusin', triggerFocusIn.bind(this, this.el))
  this.dropdown.addEventListener('focusout', triggerFocusOut.bind(this, this.el))
  window.addEventListener('click', this._onClickedOutside.bind(this))

  if (this.config.searchable) {
    this._bindSearchEvent()
  }
}

NiceSelect.prototype._bindSearchEvent = function () {
  const searchBox = this.dropdown.querySelector('.nice-select-search')
  if (searchBox) {
    searchBox.addEventListener('click', function (e) {
      e.stopPropagation()
      return false
    })
  }

  searchBox.addEventListener('input', this._onSearchChanged.bind(this))
}

NiceSelect.prototype._onClicked = function (e) {
  if (this.multiple) {
    this.dropdown.classList.add('open')
  } else {
    this.dropdown.classList.toggle('open')
  }

  if (this.dropdown.classList.contains('open')) {
    const search = this.dropdown.querySelector('.nice-select-search')
    if (search) {
      search.value = ''
      search.focus()
    }

    let t = this.dropdown.querySelector('.focus')
    removeClass(t, 'focus')
    t = this.dropdown.querySelector('.selected')
    addClass(t, 'focus')
    this.dropdown.querySelectorAll('ul li').forEach(function (item) {
      item.style.display = ''
    })
  } else {
    this.dropdown.focus()
  }
}

NiceSelect.prototype._onItemClicked = function (option, e) {
  const optionEl = e.target

  if (!hasClass(optionEl, 'disabled')) {
    if (this.multiple) {
      if (hasClass(optionEl, 'selected')) {
        removeClass(optionEl, 'selected')
        this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1)
        this.el.querySelector('option[value="' + optionEl.dataset.value + '"]').selected = false
      } else {
        addClass(optionEl, 'selected')
        this.selectedOptions.push(option)
      }
    } else {
      this.selectedOptions.forEach(function (item) {
        removeClass(item.element, 'selected')
      })

      addClass(optionEl, 'selected')
      this.selectedOptions = [option]
    }

    this._renderSelectedItems()
    this.updateSelectValue()
  }
}

NiceSelect.prototype.updateSelectValue = function () {
  if (this.multiple) {
    const select = this.el
    this.selectedOptions.forEach(function (item) {
      const el = select.querySelector('option[value="' + item.data.value + '"]')
      if (el) {
        el.setAttribute('selected', true)
      }
    })
  } else if (this.selectedOptions.length > 0) {
    this.el.value = this.selectedOptions[0].data.value
  }
  triggerChange(this.el)
}

NiceSelect.prototype._onClickedOutside = function (e) {
  if (!this.dropdown.contains(e.target)) {
    this.dropdown.classList.remove('open')
  }
}

NiceSelect.prototype._onKeyPressed = function (e) {
  // Keyboard events

  const focusedOption = this.dropdown.querySelector('.focus')

  const open = this.dropdown.classList.contains('open')

  // Space or Enter
  if (e.keyCode === 32 || e.keyCode === 13) {
    if (open) {
      triggerClick(focusedOption)
    } else {
      triggerClick(this.dropdown)
    }
  } else if (e.keyCode === 40) {
    // Down
    if (!open) {
      triggerClick(this.dropdown)
    } else {
      const next = this._findNext(focusedOption)
      if (next) {
        const t = this.dropdown.querySelector('.focus')
        removeClass(t, 'focus')
        addClass(next, 'focus')
      }
    }
    e.preventDefault()
  } else if (e.keyCode === 38) {
    // Up
    if (!open) {
      triggerClick(this.dropdown)
    } else {
      const prev = this._findPrev(focusedOption)
      if (prev) {
        const t = this.dropdown.querySelector('.focus')
        removeClass(t, 'focus')
        addClass(prev, 'focus')
      }
    }
    e.preventDefault()
  } else if (e.keyCode === 27 && open) {
    // Esc
    triggerClick(this.dropdown)
  }
  return false
}

NiceSelect.prototype._findNext = function (el) {
  if (el) {
    el = el.nextElementSibling
  } else {
    el = this.dropdown.querySelector('.list .option')
  }

  while (el) {
    if (!hasClass(el, 'disabled') && el.style.display !== 'none') {
      return el
    }
    el = el.nextElementSibling
  }

  return null
}

NiceSelect.prototype._findPrev = function (el) {
  if (el) {
    el = el.previousElementSibling
  } else {
    el = this.dropdown.querySelector('.list .option:last-child')
  }

  while (el) {
    if (!hasClass(el, 'disabled') && el.style.display !== 'none') {
      return el
    }
    el = el.previousElementSibling
  }

  return null
}

NiceSelect.prototype._onSearchChanged = function (e) {
  const open = this.dropdown.classList.contains('open')
  let text = e.target.value
  text = text.toLowerCase()

  if (text === '') {
    this.options.forEach(function (item) {
      item.element.style.display = ''
    })
  } else if (open) {
    const matchReg = new RegExp(text)
    this.options.forEach(function (item) {
      const optionText = item.data.text.toLowerCase()
      const matched = matchReg.test(optionText)
      item.element.style.display = matched ? '' : 'none'
    })
  }

  this.dropdown.querySelectorAll('.focus').forEach(function (item) {
    removeClass(item, 'focus')
  })

  const firstEl = this._findNext(null)
  addClass(firstEl, 'focus')
}

export function bind (el, options) {
  return new NiceSelect(el, options)
}

/*

This is a modified version of the <select> library, bluzky/nice-select2

---

MIT License

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

*/
